import React, { useState, useMemo } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown, Edit2, Save, X } from 'lucide-react';
import { Button } from './ui/button';

const StockTable = ({ data, columns, onSaveOrderFields = null, enableOrderEdit = false, searchTerm = '' }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [editingRow, setEditingRow] = useState(null);
  const [orderEditData, setOrderEditData] = useState({});

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') {
        direction = 'desc';
      } else if (sortConfig.direction === 'desc') {
        direction = null;
      }
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedData = useMemo(() => {
    // First filter by search term
    let filtered = data;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = data.filter(row => {
        // Search across all visible columns
        return columns.some(column => {
          if (column.hidden) return false;
          const value = row[column.key];
          return String(value || '').toLowerCase().includes(search);
        });
      });
    }

    // Then sort
    if (!sortConfig.key || !sortConfig.direction) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      // Special handling for expiry_date column (format: "MMM-YYYY" like "Jan-2027" or "YYYY-MM-DD HH:MM:SS")
      if (sortConfig.key === 'expiry_date') {
        const parseExpiryDate = (dateStr) => {
          if (!dateStr || dateStr === '' || dateStr === 'NaT') return null;
          
          const str = String(dateStr).toLowerCase().trim();
          
          // Try MMM-YYYY format first (e.g., "jan-2027")
          const monthMap = {
            'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'jun': 5,
            'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
          };
          
          const mmmYYYY = str.match(/^([a-z]{3})-(\d{4})$/);
          if (mmmYYYY) {
            const month = monthMap[mmmYYYY[1]];
            const year = parseInt(mmmYYYY[2], 10);
            if (month !== undefined && !isNaN(year)) {
              return new Date(year, month, 1);
            }
          }
          
          // Try YYYY-MM-DD format (e.g., "2025-11-03" or "2025-11-03 00:00:00")
          const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (isoMatch) {
            const year = parseInt(isoMatch[1], 10);
            const month = parseInt(isoMatch[2], 10) - 1; // JS months are 0-indexed
            const day = parseInt(isoMatch[3], 10);
            if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
              return new Date(year, month, day);
            }
          }
          
          // Try DD/MM/YYYY format
          const ddmmyyyy = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
          if (ddmmyyyy) {
            const day = parseInt(ddmmyyyy[1], 10);
            const month = parseInt(ddmmyyyy[2], 10) - 1;
            const year = parseInt(ddmmyyyy[3], 10);
            if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
              return new Date(year, month, day);
            }
          }
          
          return null;
        };

        const aDate = parseExpiryDate(aVal);
        const bDate = parseExpiryDate(bVal);

        // Handle null dates - put them at the end
        if (!aDate && !bDate) return 0;
        if (!aDate) return sortConfig.direction === 'asc' ? 1 : -1;
        if (!bDate) return sortConfig.direction === 'asc' ? -1 : 1;

        return sortConfig.direction === 'asc' 
          ? aDate.getTime() - bDate.getTime() 
          : bDate.getTime() - aDate.getTime();
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal || '').toLowerCase();
      const bStr = String(bVal || '').toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [data, sortConfig, searchTerm, columns]);

  const startOrderEdit = (row) => {
    setEditingRow(row.product_name);
    setOrderEditData({
      order_given_to: row.order_given_to || '',
      order_qty: row.order_qty || 0,
      on_date: row.on_date || ''
    });
  };

  const cancelOrderEdit = () => {
    setEditingRow(null);
    setOrderEditData({});
  };

  const saveOrderEdit = (productName) => {
    if (onSaveOrderFields) {
      onSaveOrderFields(productName, orderEditData);
    }
    setEditingRow(null);
    setOrderEditData({});
  };

  const handleOrderFieldChange = (field, value) => {
    setOrderEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400" />;
    }
    if (sortConfig.direction === 'asc') {
      return <ChevronUp className="w-3.5 h-3.5 text-blue-600" />;
    }
    return <ChevronDown className="w-3.5 h-3.5 text-blue-600" />;
  };

  const visibleColumns = columns.filter(col => !col.hidden);

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-20">
        <svg className="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-slate-600 text-lg">No data available</p>
        <p className="text-slate-500 text-sm mt-2">Upload a file to see your stock data</p>
      </div>
    );
  }

  // Get key fields for mobile display
  const getProductName = (row) => row.product_name || row.name || row.item || Object.values(row)[0];
  const getQuantity = (row) => row.available_qty || row.quantity || row.qty || row.stock || 0;
  const getExpiry = (row) => row.expiry_date || row.expiry || '';
  const getManufacturer = (row) => row.manufactured_by || row.manufacturer || row.brand || '';

  return (
    <>
      {/* Scrollable Table View */}
      <div className="overflow-x-auto print:overflow-visible" style={{ maxHeight: '70vh' }}>
      <style>{`
        @media print {
          .overflow-x-auto {
            max-height: none !important;
            overflow: visible !important;
          }
          .sticky {
            position: static !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
      <table className="w-full min-w-[900px]" data-testid="stock-table">
        <thead className="sticky top-0 z-10 bg-slate-50 border-b-2 border-slate-200 shadow-sm print:static print:bg-white">
          <tr>
            {enableOrderEdit && <th className="px-3 py-3 text-xs font-semibold text-slate-700 uppercase no-print">Actions</th>}
            {visibleColumns.map((column) => (
              <th
                key={column.key}
                className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                onClick={() => column.sortable && handleSort(column.key)}
                style={{ 
                  minWidth: column.minWidth || '120px',
                  maxWidth: column.maxWidth || 'none',
                  fontSize: '10px',
                  lineHeight: '1.2'
                }}
              >
                <div className="flex items-center justify-between space-x-1">
                  <span className="break-words">{column.label}</span>
                  {column.sortable && getSortIcon(column.key)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-slate-200">
          {filteredAndSortedData.map((row, index) => {
            const isEditing = editingRow === row.product_name;
            
            return (
              <tr key={index} className="hover:bg-slate-50 transition-colors" data-testid={`stock-row-${index}`}>
                {enableOrderEdit && (
                  <td className="px-3 py-3 no-print">
                    {!isEditing ? (
                      <Button
                        onClick={() => startOrderEdit(row)}
                        size="sm"
                        variant="outline"
                        className="h-8"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                    ) : (
                      <div className="flex space-x-1">
                        <Button
                          onClick={() => saveOrderEdit(row.product_name)}
                          size="sm"
                          className="h-8 bg-green-600 hover:bg-green-700"
                        >
                          <Save className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          onClick={cancelOrderEdit}
                          size="sm"
                          variant="outline"
                          className="h-8"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}
                  </td>
                )}
                
                {visibleColumns.map((column) => (
                  <td
                    key={column.key}
                    className="px-3 py-3 text-sm text-slate-700"
                    style={{ 
                      minWidth: column.minWidth || '120px',
                      maxWidth: column.maxWidth || 'none'
                    }}
                  >
                    {column.editableField && isEditing ? (
                      <input
                        type={column.type === 'number' ? 'number' : column.key === 'on_date' ? 'date' : 'text'}
                        value={orderEditData[column.key] ?? row[column.key] ?? ''}
                        onChange={(e) => handleOrderFieldChange(column.key, e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-blue-400 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : column.render ? (
                      column.render(row)
                    ) : (
                      <span className={column.type === 'number' ? 'font-medium' : ''}>
                        {column.format ? column.format(row[column.key]) : (row[column.key] ?? '-')}
                      </span>
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
};

export default StockTable;
